import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, FileText, Printer, Table2 } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { cn } from '@/lib/utils';
import { acaoTexto } from '@/componentes/ui/botao';
import { Gaveta } from '@/componentes/ui/gaveta';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { imprimirPapel } from '@/lib/imprimirPapel';
import { CabecalhoColuna, useGrade, type ColunaGrade } from '@/componentes/tabela/GradeFiltravel';
import { CSS_ESPELHO, CSS_ESPELHO_PAPEL, DATA_ISO, dataPbs, EspelhoFolha } from './EspelhoFolha';

/**
 * Os auxiliares do formulário NF_COMPRA do PBS (Contábil, Fiscal, Espelho,
 * Estoque…). A lista, o SQL e os títulos das colunas vêm do dicionário a cada
 * clique (API `nfCompraAuxiliares.ts`): nada fixo aqui, nem a lista de botões
 * nem as colunas. Mudou no PBS, muda aqui.
 */

interface Auxiliar {
  ordem: number;
  descricao: string;
  tipo: string;
  /** Espelho da NF: sai como folha fiscal. */
  folha: boolean;
}

interface Campo {
  campo: string;
  titulo: string;
  totalizar: boolean;
  decimais: number | null;
}

interface Parte {
  nome: string;
  colunas: string[];
  linhas: Record<string, unknown>[];
  campos: Campo[];
  truncado: boolean;
  erro?: string;
  /** 0 = principal; 1.. = sub-relatório. */
  sub: number;
}

export function AuxiliaresNfCompra({ nfCompra }: { nfCompra: number }): JSX.Element | null {
  const lista = useQuery<{ auxiliares: Auxiliar[] }>({
    queryKey: ['nf-compra-auxiliares'],
    queryFn: () => api.get(`/api/entradas/nf-compra/${nfCompra}/auxiliares`),
    staleTime: 60_000,
  });
  const [aberto, definirAberto] = React.useState<Auxiliar | null>(null);
  if (lista.isError) {
    return <p className="text-2xs text-status-divergencia">Consultas indisponíveis: {lista.error instanceof ErroHttp ? lista.error.message : String(lista.error)}</p>;
  }
  const auxiliares = lista.data?.auxiliares ?? [];
  if (!auxiliares.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {auxiliares.map((a) => (
        <button key={a.ordem} type="button" onClick={() => definirAberto(a)} className={cn(acaoTexto, 'text-xs')}>
          {a.tipo === 'R' ? <FileText className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
          {a.descricao}
        </button>
      ))}
      {aberto ? <ResultadoAuxiliar nfCompra={nfCompra} auxiliar={aberto} aoFechar={() => definirAberto(null)} /> : null}
    </div>
  );
}

function ResultadoAuxiliar({ nfCompra, auxiliar, aoFechar }: { nfCompra: number; auxiliar: Auxiliar; aoFechar: () => void }): JSX.Element {
  const r = useQuery<{ partes: Parte[] }>({
    queryKey: ['nf-compra-auxiliar', nfCompra, auxiliar.ordem],
    queryFn: () => api.get(`/api/entradas/nf-compra/${nfCompra}/auxiliares/${auxiliar.ordem}`),
  });
  const papel = React.useRef<HTMLDivElement>(null);
  const [copiado, definirCopiado] = React.useState(false);
  const titulo = `${auxiliar.descricao} · NF de compra ${nfCompra}`;
  const partes = r.data?.partes ?? [];
  // O espelho da NF (relatório do PBS) sai como folha de documento fiscal; os dados continuam vindo do SQL do dicionário.
  const folha = auxiliar.folha && Boolean(partes[0]?.linhas.length);
  const copiar = (): void => {
    const tsv = partes
      .filter((p) => p.linhas.length)
      .map((p) => {
        const cols = colunasDe(p);
        return [cols.map((c) => c.titulo).join('\t'), ...p.linhas.map((l) => cols.map((c) => texto(l[c.campo], c)).join('\t'))].join('\n');
      })
      .join('\n\n');
    void navigator.clipboard.writeText(tsv).then(() => {
      definirCopiado(true);
      setTimeout(() => definirCopiado(false), 1500);
    });
  };
  return (
    <Gaveta
      aberta
      aoFechar={aoFechar}
      titulo={auxiliar.descricao}
      subtitulo={`NF de compra ${nfCompra}`}
      className="sm:w-[min(1200px,100vw)]"
      acoes={
        partes.some((p) => p.linhas.length) ? (
          <div className="flex items-center gap-1.5">
            <button type="button" className={cn(acaoTexto, 'text-xs')} onClick={copiar}>
              <Copy className="h-3.5 w-3.5" /> {copiado ? 'Copiado' : 'Copiar para planilha'}
            </button>
            <button
              type="button"
              className={cn(acaoTexto, 'text-xs')}
              onClick={() =>
                papel.current &&
                imprimirPapel(
                  folha
                    ? { conteudo: papel.current, titulo, css: CSS_ESPELHO_PAPEL, seletor: '.espelho' }
                    : { conteudo: papel.current, titulo, css: CSS_PAPEL, seletor: '.papel-aux' },
                )
              }
            >
              <Printer className="h-3.5 w-3.5" /> Imprimir
            </button>
          </div>
        ) : undefined
      }
    >
      {r.isLoading ? <Esqueleto className="h-40" /> : null}
      {r.isError ? (
        <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
          {r.error instanceof ErroHttp ? r.error.message : String(r.error)}
        </p>
      ) : null}
      {r.data && !partes.some((p) => p.linhas.length) && !partes.some((p) => p.erro) ? (
        <p className="text-[13px] text-textoFraco">Nada para esta NF de compra.</p>
      ) : null}
      {folha ? (
        <div className="overflow-x-auto rounded-lg border border-borda bg-white p-4 shadow-sm">
          <style>{CSS_ESPELHO}</style>
          <div ref={papel}>
            <EspelhoFolha partes={partes} nfCompra={nfCompra} />
          </div>
          {partes.filter((p) => p.erro).map((p) => (
            <TabelaParte key={p.nome} parte={p} mostrarNome />
          ))}
        </div>
      ) : (
        <div ref={papel}>
          <div className="papel-aux space-y-4">
            <h1 className="papel-titulo hidden">{titulo}</h1>
            {partes.map((p, i) => (
              <TabelaParte key={`${p.nome}-${i}`} parte={p} mostrarNome={partes.length > 1} />
            ))}
          </div>
        </div>
      )}
    </Gaveta>
  );
}

/** As colunas na ordem do dicionário (CONSULTAS_CAMPOS) quando ele as define; senão, as do resultado. */
function colunasDe(p: Parte): Campo[] {
  const doDicionario = p.campos.filter((c) => p.colunas.includes(c.campo));
  if (doDicionario.length) return doDicionario;
  return p.colunas.map((c) => ({ campo: c, titulo: c.replace(/_/g, ' '), totalizar: false, decimais: null }));
}


function texto(v: unknown, c: Campo): string {
  if (v == null) return '';
  if (typeof v === 'number') {
    // Formato do dicionário quando ele define; inteiro sem formato é código (centro, documento, produto): sem ponto de milhar.
    if (c.decimais != null) return v.toLocaleString('pt-BR', { minimumFractionDigits: c.decimais, maximumFractionDigits: c.decimais });
    if (Number.isInteger(v)) return String(v);
    return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }
  if (typeof v === 'string' && DATA_ISO.test(v)) return dataPbs(v);
  return String(v);
}


function TabelaParte({ parte, mostrarNome }: { parte: Parte; mostrarNome: boolean }): JSX.Element | null {
  if (parte.erro) {
    return (
      <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">
        {parte.nome}: {parte.erro}
      </p>
    );
  }
  if (!parte.linhas.length) return null;
  return <GradeParte parte={parte} mostrarNome={mostrarNome} />;
}

type LinhaNumerada = { l: Record<string, unknown>; i: number };

/**
 * A consulta numa grade com o autofiltro de planilha (a mesma do Kardex):
 * clique no título ordena, o funil filtra por valores. Os totais (os que o
 * dicionário manda totalizar) somam só o que está à vista.
 */
function GradeParte({ parte, mostrarNome }: { parte: Parte; mostrarNome: boolean }): JSX.Element {
  const cols = React.useMemo(() => colunasDe(parte), [parte]);
  const linhas = React.useMemo<LinhaNumerada[]>(() => parte.linhas.map((l, i) => ({ l, i })), [parte]);
  const colunas = React.useMemo<ColunaGrade<LinhaNumerada>[]>(
    () =>
      cols.map((c) => {
        const numerica = parte.linhas.some((l) => typeof l[c.campo] === 'number');
        // Coluna de texto longo (descrição do item): quebra em linhas em vez de esticar a tabela.
        const longa = parte.linhas.some((l) => typeof l[c.campo] === 'string' && (l[c.campo] as string).length > 32);
        return {
          chave: c.campo,
          rotulo: c.titulo,
          ...(numerica ? { alinhar: 'direita' as const } : {}),
          ...(longa ? { largura: true } : {}),
          ordenar: (x) => {
            const v = x.l[c.campo];
            return typeof v === 'number' ? v : v == null ? '' : String(v);
          },
          texto: (x) => texto(x.l[c.campo], c),
        };
      }),
    [cols, parte],
  );
  const grade = useGrade(linhas, colunas, (a, b) => a.i - b.i);
  const totais = cols.map((c) => (c.totalizar ? grade.visiveis.reduce((s, x) => s + (typeof x.l[c.campo] === 'number' ? (x.l[c.campo] as number) : 0), 0) : null));
  return (
    <section className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {mostrarNome ? <h3 className="papel-secao text-xs font-bold uppercase tracking-wide text-textoSuave">{parte.nome}</h3> : <span />}
        {!grade.intacta ? (
          <button type="button" className={cn(acaoTexto, 'text-2xs')} onClick={grade.limpar}>
            Limpar filtros · {grade.visiveis.length} de {linhas.length}
          </button>
        ) : null}
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-borda">
        <table className="w-full text-xs">
          <thead className="text-left">
            <tr>
              {colunas.map((c) => (
                <CabecalhoColuna key={c.chave} coluna={c} grade={grade} />
              ))}
            </tr>
          </thead>
          <tbody>
            {grade.visiveis.map((x) => (
              <tr key={x.i} className="border-t border-borda">
                {colunas.map((c, j) => (
                  <td
                    key={c.chave}
                    className={cn(
                      'px-2 py-1 align-top',
                      c.largura ? 'min-w-[200px] max-w-[320px] whitespace-normal break-words' : 'whitespace-nowrap',
                      c.alinhar === 'direita' && 'text-right tabular-nums',
                    )}
                  >
                    {texto(x.l[c.chave], cols[j]!)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totais.some((t) => t != null) ? (
            <tfoot className="border-t-2 border-bordaForte font-semibold">
              <tr>
                {cols.map((c, i) => (
                  <td key={c.campo} className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                    {totais[i] != null ? texto(totais[i], c) : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      {parte.truncado ? <p className="text-2xs text-textoSuave">Mostrando as primeiras linhas.</p> : null}
    </section>
  );
}

const CSS_PAPEL = `
  @page { size: A4 landscape; margin: 8mm; }
  body { font-family: Arial, sans-serif; font-size: 7.5pt; }
  .papel-titulo { display: block !important; font-size: 11pt; margin: 0 0 3mm; }
  .papel-secao { font-size: 7.5pt; text-transform: uppercase; margin: 4mm 0 1mm; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 6pt; text-transform: uppercase; background: #f0f0f0; border-bottom: .5pt solid #888; padding: .7mm 1mm; }
  td { padding: .5mm 1mm; border-bottom: .25pt solid #ddd; white-space: nowrap; }
  td.text-right { text-align: right; }
  td.whitespace-normal { white-space: normal; max-width: 70mm; }
  tfoot td { font-weight: bold; border-top: .75pt solid #000; }
`;
